"use client";

import { ArrowRight, Instagram, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
const tickerItems = ["CATALYST GROWTH", "ROI DRIVEN", "DATA SCIENCE", "MARKET DISRUPTION", "CATALYST GROWTH", "ROI DRIVEN", "DATA SCIENCE", "MARKET DISRUPTION"];
const socials = [{
  name: "Instagram",
  href: "#",
  icon: Instagram
}, {
  name: "TikTok",
  href: "#",
  icon: Twitter
}, {
  name: "X",
  href: "#",
  icon: Twitter
}, {
  name: "Pinterest",
  href: "#",
  icon: Instagram
}];
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map(i => <div key={i} data-index={i} className="h-5 w-24 bg-muted/30 animate-pulse" />)}
      </div>;
  }
  return <nav aria-label="Footer navigation">
      <ul className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
        {menuItems.map((item, i) => <li data-index={i} key={item.id}>
            <Link to={item.href || "#"} className="text-foreground inline-block font-default text-sm text-foreground/90 transition-colors hover:text-primary" style={{
          backgroundImage: "linear-gradient(currentColor, currentColor)",
          backgroundSize: "0% 2px",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "0 100%",
          paddingBottom: "4px",
          transition: "background-size 240ms ease, color 240ms ease"
        }} onMouseEnter={e => {
          (e.currentTarget as HTMLElement).style.backgroundSize = "100% 2px";
        }} onMouseLeave={e => {
          (e.currentTarget as HTMLElement).style.backgroundSize = "0% 2px";
        }}>
              {item.label}
            </Link>
          </li>)}
      </ul>
    </nav>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-5">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="link" className="mt-3 h-auto p-0 text-foreground/80 hover:text-primary">
          Sign up another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="first_name" render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input placeholder="First Name (optional)" {...field} className="h-10 border-0 border-b border-border bg-transparent px-0 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <div className="relative flex items-center">
                <Input type="email" placeholder="your@email.com" {...field} className="h-10 border-0 border-b border-border bg-transparent px-0 pr-12 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0" />
                <Button type="submit" size="icon" disabled={isSubmitting} aria-label="Subscribe" className="absolute right-0 size-9 bg-primary text-primary-foreground hover:bg-primary/90">
                  <ArrowRight className="size-4" />
                </Button>
              </div>
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <div className="flex items-start gap-3">
        <FormField control={control} name="agree_marketing" render={({
        field
      }) => <FormItem className="flex items-start gap-3 space-y-0">
              <FormControl>
                <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref} className="mt-1 size-4 cursor-pointer accent-primary" />
              </FormControl>
              <label htmlFor={field.name} className="font-default text-xs leading-relaxed text-muted-foreground">
                Receive growth insights and strategy updates.
              </label>
            </FormItem>} />
      </div>

      {submitError && <p className="font-mono text-xs uppercase tracking-[0.18em] text-destructive">
          {submitError}
        </p>}
    </Form>;
}
export default function CatalystGrowthFooter() {
  return <section data-section-id="1524" id="the-lab-footer" className="relative bg-background text-foreground">
      {}
      <div className="overflow-hidden border-b border-border py-3">
        <div className="flex items-center gap-10 whitespace-nowrap" style={{
        animation: "catalystTicker 60s linear infinite"
      }}>
          {[...tickerItems, ...tickerItems].map((item, i) => <span data-index={i} key={i} className="flex items-center gap-10 font-mono text-[13px] uppercase tracking-[0.18em] text-primary">
              {item}
              <span aria-hidden className="text-primary/70"></span>
            </span>)}
        </div>
        <style>{`
          @keyframes catalystTicker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-5 pt-24 pb-10 md:px-14">
        {}
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-12">
          {}
          <div className="lg:col-span-8">
            <Link to="/" aria-label="Catalyst Growth Lab — home" className="inline-block">
              <h2 className="font-default font-bold leading-[0.92] tracking-tight text-foreground" style={{
              fontSize: "clamp(3rem, 7vw, 6rem)"
            }}>
                CATALYST{" "}
                <span className="italic text-primary" style={{
                fontFamily: "var(--font-default)"
              }}>
                  Growth
                </span>
              </h2>
            </Link>

            <div className="mt-12">
              <p className="mb-6 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Lab №. 01 / Navigate the ecosystem
              </p>
              <MenuProvider menu_id="32" className="footer-menu">
                <FooterMenuConsumer />
              </MenuProvider>
            </div>
          </div>

          {}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2">
              <span className="inline-block border border-border px-2 py-1 font-mono text-xs uppercase tracking-[0.18em]">
                №. 01
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Performance Lab
              </span>
            </div>

            <address className="mt-6 not-italic font-mono text-xs uppercase leading-relaxed tracking-[0.14em] text-foreground/80">
              CATALYST GROWTH LAB
              <br />
              <span aria-hidden></span> 100 INNOVATION WAY
              <br />
              <span aria-hidden></span> TECH DISTRICT, NY
            </address>

            <div className="mt-6 space-y-1 font-default text-sm text-foreground/80">
              <p>hello@catalystgrowth.lab</p>
              <p>+1 555-GROWTH</p>
            </div>

            <div className="mt-8 flex items-center gap-3">
              {socials.map((social, i) => <Link data-index={i} key={social.name} to={social.href} aria-label={social.name} className="grid size-10 place-items-center border border-border text-foreground transition-colors hover:border-primary hover:text-primary">
                  <social.icon className="size-4" strokeWidth={1.5} />
                </Link>)}
            </div>

            <div className="mt-10">
              <p className="mb-4 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Join the growth list
              </p>
              <FormProvider formKey="contact_form_home" formId="contact_form_home" sectionName="the-lab-footer" defaultValues={{
              first_name: "",
              email: "",
              agree_marketing: false
            }} validationRules={{
              email: {
                required: true,
                pattern: "email"
              }
            }} submitText="Subscribe" successMessage="You're in the loop. Strategy incoming." errorMessage="Connection failed. Please try again.">
                <NewsletterForm />
              </FormProvider>
            </div>
          </div>
        </div>

        {}
        <div className="mt-16 flex justify-center">
          <span className="relative inline-flex items-center gap-2 bg-secondary px-5 py-2 font-mono text-xs uppercase tracking-[0.18em] text-foreground" style={{
          boxShadow: "inset 0 0 0 1.5px hsl(240 5% 15%)",
          outline: "1.5px dashed hsl(240 5% 15%)",
          outlineOffset: "-8px"
        }}>
            Engineered for ROI <span aria-hidden></span> Est. 2025
          </span>
        </div>

        {}
        <div className="relative mt-12 flex items-center">
          <span className="size-1.5 bg-primary" aria-hidden />
          <Separator className="flex-1 bg-border" />
          <span className="size-1.5 bg-primary" aria-hidden />
        </div>

        {}
        <div className="mt-6 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
            © 2025 CATALYST GROWTH LAB <span aria-hidden></span> All rights reserved
          </p>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Built for scale <span aria-hidden></span> Global reach
          </p>
        </div>
      </div>
    </section>;
}